import type { QuestionResult } from '@/state/surveys/useSurveyResults';
import type { VariantBreakdown as Variant } from '@/state/surveys/useSurveyVariantBreakdown';
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSurveyVariantBreakdown } from '@/state/surveys/useSurveyVariantBreakdown';

type VariantBreakdownProps = {
  surveyId: number;
  /** The experiment the survey is tied to, which opens the panel from the start. */
  linkedExperimentId: number | null;
};

type ChoiceTableProps = { question: QuestionResult; variants: Variant[]; index: number };

const TH = 'micro-label px-3 py-2 text-left font-normal';
const TD = 'text-foreground num px-3 py-2 text-meta';

// Words, not a dash, for an empty value.
const share = (count: number, of: number) => (of > 0 ? `${Math.round((count / of) * 100)}%` : 'No answers');

const headlineCaption = (question: QuestionResult) => (question.scale === 'NPS' ? 'net score' : 'average');

/** One choice question: its options down the side, a column per variant, share then count. */
const ChoiceTable = ({ question, variants, index }: ChoiceTableProps) => {
  // Every variant's options, in order of first appearance: an option since renamed
  // can have answers in one variant only, and must still get its row.
  const optionValues = [
    ...new Set(
      variants
        .flatMap(
          (variant) =>
            variant.questions.find((candidate) => candidate.questionId === question.questionId)?.counts ?? [],
        )
        .map((count) => count.value),
    ),
  ];
  return (
    <div className="bg-panel2 border-line rounded-card overflow-x-auto border">
      <p className="text-foreground m-0 px-3 pt-3 text-meta break-words">
        {index + 1}. {question.label}
      </p>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-line border-b">
            <th className={TH}>Option</th>
            {variants.map((variant) => (
              <th key={variant.variantId} className={TH}>
                {variant.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {optionValues.map((value) => (
            <tr key={value} className="border-line border-b last:border-b-0">
              <td className="text-foreground px-3 py-2 text-meta break-words">{value}</td>
              {variants.map((variant) => {
                const own = variant.questions.find((candidate) => candidate.questionId === question.questionId);
                const count = own?.counts.find((candidate) => candidate.value === value)?.count ?? 0;
                return (
                  <td key={variant.variantId} className={TD}>
                    {share(count, own?.answered ?? 0)}
                    <span className="text-dim ml-1.5 text-micro">{count}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Results split by the variant each visitor was in, for one experiment.
 *
 * Descriptive only: no significance test and no "winner". Survey response counts
 * are small, and a variant under the low-sample threshold carries a warning rather
 * than a verdict. Tables rather than charts, because the comparison is read across
 * a row, and every number is written out.
 */
export const VariantBreakdown = ({ surveyId, linkedExperimentId }: VariantBreakdownProps) => {
  // Open from the start when the survey is tied to an experiment, which is what
  // tying it is for. Otherwise closed: the breakdown is three grouped scans and a
  // list lookup, and most visits to the tab never split by an experiment.
  const [isOpen, setIsOpen] = useState(linkedExperimentId !== null);
  const { experiments, experimentId, setExperimentId, breakdown, isListLoading, isLoading, isError } =
    useSurveyVariantBreakdown({ surveyId, enabled: isOpen });

  if (!isOpen) {
    return (
      <section className="bg-panel border-line rounded-panel flex items-center justify-between gap-3 border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-foreground m-0 text-body font-medium">By variant</h2>
          <p className="supporting m-0">Split these results by the experiment variant each visitor was in.</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(true)}
          className="border-input h-9 shrink-0 rounded-lg bg-transparent"
        >
          Compare by variant
        </Button>
      </section>
    );
  }

  if (!isListLoading && !isError && experiments.length === 0) {
    return (
      <section className="bg-panel border-line rounded-panel border px-4 py-3" data-tour="survey-results-variants">
        <h2 className="text-foreground m-0 text-body font-medium">By variant</h2>
        <p className="supporting m-0">The visitors who saw this survey were not in any experiment.</p>
      </section>
    );
  }

  const variants = breakdown?.variants ?? [];
  const template = variants[0]?.questions ?? [];
  const scoreQuestions = template.filter((question) => question.kind === 'score');
  const choiceQuestions = template.filter(
    (question) => question.kind === 'single choice' || question.kind === 'multiple choice',
  );
  const anyLowSample = variants.some((variant) => variant.lowSample);

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="survey-results-variants">
      <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-foreground m-0 text-body font-medium">By variant</h2>
        <select
          value={experimentId ?? ''}
          disabled={experiments.length === 0}
          onChange={(ev) => setExperimentId(Number(ev.target.value))}
          aria-label="Experiment to split by"
          className="border-line bg-field text-foreground h-9 rounded-lg border px-2 text-body"
        >
          {experiments.map((experiment) => (
            <option key={experiment.id} value={experiment.id}>
              {experiment.name} ({experiment.status})
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-3 p-3">
        {isLoading && !breakdown && <p className="supporting m-0">Loading the breakdown…</p>}
        {isError && (
          <p className="text-danger m-0 text-micro">The breakdown could not be loaded. Try again in a moment.</p>
        )}
        {anyLowSample && breakdown && (
          <p className="supporting m-0 flex items-start gap-2">
            <AlertTriangle className="text-warn mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Variants marked "few answers" have under {breakdown.lowSample} respondents. Differences there are more
            likely noise than a real effect.
          </p>
        )}

        <div className="bg-panel2 border-line rounded-card overflow-x-auto border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-line border-b">
                <th className={TH}>Variant</th>
                <th className={TH}>Shown</th>
                <th className={TH}>Answered</th>
                <th className={TH}>Response rate</th>
                {scoreQuestions.map((question) => (
                  <th key={question.questionId} className={TH} title={question.label}>
                    Q{template.indexOf(question) + 1} {headlineCaption(question)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => (
                <tr key={variant.variantId} className="border-line border-b last:border-b-0">
                  <td className="text-foreground px-3 py-2 text-meta">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {variant.name}
                      {variant.isControl && <span className="micro-label">control</span>}
                      {variant.lowSample && (
                        <span className="micro-label rounded-pill border-warn-line text-warn border px-1.5">
                          few answers
                        </span>
                      )}
                    </span>
                  </td>
                  <td className={TD}>{variant.impressions}</td>
                  <td className={TD}>{variant.respondents}</td>
                  <td className={TD}>{variant.responseRate === null ? 'Not shown' : `${variant.responseRate}%`}</td>
                  {scoreQuestions.map((question) => {
                    const own = variant.questions.find((candidate) => candidate.questionId === question.questionId);
                    return (
                      <td key={question.questionId} className={TD}>
                        {own?.headline ?? 'No answers'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {choiceQuestions.map((question) => (
          <ChoiceTable
            key={question.questionId}
            question={question}
            variants={variants}
            index={template.indexOf(question)}
          />
        ))}
      </div>
    </section>
  );
};
